// @@PORTAL_PAY_v124@@ Client portal — "Pay invoices" (inside the Invoices tab).
// @@PORTAL_PAY_v124_1@@ + pay by check, and the header's green "Make a Payment" button opens this modal (autoOpen).
// @@PORTAL_PAY_v124_2@@ Wise is its own option (separate from the ACH bank transfer) with its own "I've paid" step.
// Pick open invoices (partial amounts allowed), then pay by card (Zoho Payments checkout widget, card fee
// shown up front and refunded automatically on debit cards) or by bank transfer / Wise (shows XYZ's details;
// "I've sent it" lets us know). The server re-checks every amount against Zoho and computes the fee itself.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';

const ZPAY_SRC = 'https://static.zohocdn.com/zpay/zpay-js/v1/zpayments.js';
const OPENISH = new Set(['open', 'overdue', 'partially_paid']);
const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
function money(n, ccy) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: ccy || 'USD' }).format(Number(n)); }
  catch (e) { return `$${Number(n).toFixed(2)}`; }
}
function fmtYmd(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s || ''));
  if (!m) return '—';
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
}
const todayYmd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
async function post(path, body) {
  const r = await apiFetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  return { r, j };
}
let _zpayLoading = null;
function loadZPay() {
  if (window.ZPayments) return Promise.resolve();
  if (_zpayLoading) return _zpayLoading;
  _zpayLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = ZPAY_SRC; s.async = true;
    s.onload = () => (window.ZPayments ? resolve() : reject(new Error('widget')));
    s.onerror = () => { _zpayLoading = null; reject(new Error('widget')); };
    document.head.appendChild(s);
  });
  return _zpayLoading;
}

function PayModal({ clientId, ccy, open, opts, onClose, onDone }) {
  const [sel, setSel] = useState(() => {
    const anyOverdue = open.some((i) => i.status === 'overdue');
    const o = {}; open.forEach((i) => { o[i.number] = { on: anyOverdue ? i.status === 'overdue' : true, amt: String(round2(i.balance).toFixed(2)) }; }); return o;
  });
  const [step, setStep] = useState('select');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const [sentOn, setSentOn] = useState(todayYmd());
  const [note, setNote] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) (step === 'done' ? onDone : onClose)(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose, onDone, step]);

  const lines = useMemo(() => open.filter((i) => sel[i.number] && sel[i.number].on).map((i) => ({
    number: i.number, balance: Number(i.balance), amount: round2(Number(sel[i.number].amt)),
  })), [open, sel]);
  const bad = lines.find((l) => !(l.amount > 0) || l.amount > round2(l.balance) + 0.004);
  const subtotal = round2(lines.reduce((a, l) => a + (l.amount > 0 ? l.amount : 0), 0));
  const feePct = opts.card.enabled ? Number(opts.card.fee_pct) || 0 : 0;
  const fee = round2(subtotal * feePct / 100);
  const total = round2(subtotal + fee);
  const items = lines.map((l) => ({ number: l.number, amount: l.amount }));
  const canGo = lines.length > 0 && !bad;

  function setAmt(num, v) { setSel((s) => ({ ...s, [num]: { ...s[num], amt: v } })); }
  function toggle(num) { setSel((s) => ({ ...s, [num]: { ...s[num], on: !s[num].on } })); }
  function applyQuote(q) {
    if (!q || !q.invoices) return;
    setSel((s) => { const o = { ...s }; q.invoices.forEach((l) => { if (o[l.number]) o[l.number] = { ...o[l.number], amt: String(Number(l.amount).toFixed(2)) }; }); return o; });
  }

  async function payCard() {
    setBusy(true); setErr('');
    let instance = null;
    try {
      const { r, j } = await post('/api/portal/pay/card/session', { client_id: clientId, items, expected_total: total });
      if (r.status === 409 && j.quote) { applyQuote(j.quote); setErr(j.error || 'Your balance changed — please review the new total.'); return; }
      if (!r.ok) { setErr(j.error || 'We couldn’t start the card payment. Please try again shortly.'); return; }
      try { await loadZPay(); } catch (e) { setErr('The secure card form couldn’t load. Check your connection (or ad blocker) and try again.'); return; }
      instance = new window.ZPayments({ account_id: j.widget.account_id, domain: j.widget.domain, otherOptions: { api_key: j.widget.api_key } });
      let data;
      try {
        data = await instance.requestPaymentMethod({
          amount: String(j.amount), transaction_type: 'payment', currency_code: j.currency_code, payments_session_id: j.session_id,
          currency_symbol: '$', business: j.business, description: j.description, invoice_number: j.invoice_number,
        });
      } catch (e) {
        if (e && e.code === 'widget_closed') { setErr('Payment cancelled — nothing was charged.'); return; }
        setErr((e && e.message) || 'The card payment didn’t go through. Nothing was charged.');
        return;
      }
      if (!data || !data.payment_id) { setErr('The card payment didn’t go through. Nothing was charged.'); return; }
      const c = await post('/api/portal/pay/card/confirm', { client_id: clientId, ref: j.ref, payment_id: data.payment_id });
      if (c.r.status === 202 || (c.r.ok && c.j.status === 'paid')) { setResult({ kind: 'card', processing: c.r.status === 202, p: c.j.payment, msg: c.j.message }); setStep('done'); return; }
      setErr(c.j.error || 'We couldn’t confirm the payment. If your card was charged you’ll get a receipt by email — otherwise please try again.');
    } catch (e) {
      setErr('Something went wrong. If your card was charged you’ll get a receipt by email.');
    } finally {
      if (instance) { try { await instance.close(); } catch (e) { /* already closed */ } }
      setBusy(false);
    }
  }

  async function reportBank(method) {
    setBusy(true); setErr('');
    try {
      const m = method === 'check' || method === 'wise' ? method : 'bank';
      const { r, j } = await post('/api/portal/pay/bank/report', { client_id: clientId, items, sent_on: sentOn, note, method: m });
      if (!r.ok) { setErr(j.error || 'We couldn’t send that. Please email info@xyzmerch.com instead.'); return; }
      setResult({ kind: m, p: j.payment }); setStep('done');
    } catch (e) { setErr('We couldn’t send that. Please email info@xyzmerch.com instead.'); }
    finally { setBusy(false); }
  }

  async function copyDetails() {
    try { await navigator.clipboard.writeText(opts.bank.details); setCopied(true); setTimeout(() => setCopied(false), 1800); } catch (e) { /* no clipboard */ }
  }

  const Summary = ({ withFee }) => (
    <div className="cp-pay-sum">
      {lines.map((l) => <div key={l.number}><span>Invoice {l.number}{l.amount < l.balance - 0.004 ? ' (partial)' : ''}</span><span>{money(l.amount, ccy)}</span></div>)}
      {withFee && feePct > 0 && <div><span>Card fee ({feePct}%)</span><span>{money(fee, ccy)}</span></div>}
      <div className="cp-pay-total"><span>{withFee ? 'Total' : 'Amount to send'}</span><span>{money(withFee ? total : subtotal, ccy)}</span></div>
    </div>
  );

  return (
    <div className="cp-pay-backdrop" role="dialog" aria-modal="true" aria-label="Pay invoices"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) (step === 'done' ? onDone : onClose)(); }}>
      <div className="cp-pay-modal">
        <div className="cp-pay-head">
          <h2>{step === 'done' ? (result && result.kind !== 'card' ? 'Thanks — we’ll watch for it' : 'Payment received') : 'Pay invoices'}</h2>
          <button type="button" className="cp-pay-x" aria-label="Close" disabled={busy} onClick={step === 'done' ? onDone : onClose}>×</button>
        </div>

        {step === 'select' && (
          <div className="cp-pay-body">
            <p className="cp-pay-hint">Choose the invoices to pay. You can pay part of an invoice by changing its amount.</p>
            <div className="cp-pay-list">
              {open.map((i) => {
                const s = sel[i.number] || {};
                return (
                  <label key={i.number} className={'cp-pay-row' + (s.on ? ' is-on' : '')}>
                    <input type="checkbox" checked={!!s.on} onChange={() => toggle(i.number)} />
                    <span className="cp-pay-inv">
                      <b>{i.number}</b>
                      <span className={i.status === 'overdue' ? 'cp-pay-overdue' : ''}>Due {fmtYmd(i.due_date)}{i.status === 'overdue' ? ' · overdue' : ''}</span>
                      <span>Balance {money(i.balance, ccy)}</span>
                    </span>
                    <span className="cp-pay-amt" onClick={(e) => e.preventDefault()}>
                      <span>$</span>
                      <input className="cp-input" type="number" step="0.01" min="0.01" max={i.balance} inputMode="decimal" disabled={!s.on}
                        value={s.amt || ''} onChange={(e) => setAmt(i.number, e.target.value)} aria-label={'Amount for ' + i.number} />
                    </span>
                  </label>
                );
              })}
            </div>
            {bad && <p className="cp-inv-msg" style={{ margin: '10px 0 0' }}>The amount for {bad.number} must be more than $0 and no more than its balance.</p>}
            <div className="cp-pay-choose">
              <div className="cp-pay-sub">Paying <b>{money(subtotal, ccy)}</b> on {lines.length} invoice{lines.length === 1 ? '' : 's'}</div>
              <div className="cp-pay-methods">
                {opts.card.enabled && (
                  <button type="button" className="cp-pay-method" disabled={!canGo} onClick={() => { setErr(''); setStep('card'); }}>
                    <b>Pay by card</b>
                    <span>{feePct > 0 ? `${feePct}% card fee on credit cards · no fee on debit cards` : 'No card fee'}</span>
                  </button>
                )}
                {opts.bank.enabled && (
                  <button type="button" className="cp-pay-method" disabled={!canGo} onClick={() => { setErr(''); setStep('bank'); }}>
                    <b>Bank transfer (ACH)</b>
                    <span>No fee · send from your own bank</span>
                  </button>
                )}
                {opts.wise && opts.wise.enabled && (
                  <button type="button" className="cp-pay-method" disabled={!canGo} onClick={() => { setErr(''); setStep('wise'); }}>
                    <b>Pay with Wise</b>
                    <span>No fee · pay through Wise</span>
                  </button>
                )}
                {opts.check && opts.check.enabled && (
                  <button type="button" className="cp-pay-method" disabled={!canGo} onClick={() => { setErr(''); setStep('check'); }}>
                    <b>Pay by check</b>
                    <span>No fee · mail a check</span>
                  </button>
                )}
              </div>
              {opts.card.demo && <p className="cp-pay-hint">Card payments are turned off on the demo account.</p>}
            </div>
          </div>
        )}

        {step === 'card' && (
          <div className="cp-pay-body">
            <Summary withFee />
            {feePct > 0 && (
              <p className="cp-pay-hint">
                A {feePct}% surcharge applies to credit card payments. No surcharge applies to debit cards — if you pay with a debit card,
                the {money(fee, ccy)} fee is refunded to your card automatically. Bank transfers have no fee.
              </p>
            )}
            <p className="cp-pay-hint">Your card details are entered in Zoho Payments’ secure form — they never touch our servers.</p>
            {err && <p className="cp-inv-msg">{err}</p>}
            <div className="cp-pay-foot">
              <button type="button" className="cp-pill" disabled={busy} onClick={() => { setErr(''); setStep('select'); }}>Back</button>
              <button type="button" className="cp-btn-sm cp-pay-go" disabled={busy || !canGo} onClick={payCard}>{busy ? 'Processing…' : `Pay ${money(total, ccy)}`}</button>
            </div>
          </div>
        )}

        {step === 'bank' && (
          <div className="cp-pay-body">
            <Summary withFee={false} />
            <div className="cp-pay-bank">
              <div className="cp-pay-bank-head"><span className="cp-k">Send to</span>
                {opts.bank.details && <button type="button" className="cp-pill" onClick={copyDetails}>{copied ? 'Copied' : 'Copy details'}</button>}</div>
              <pre>{opts.bank.details}</pre>
              <p className="cp-pay-hint">Please include the invoice number{lines.length === 1 ? '' : 's'} ({lines.map((l) => l.number).join(', ')}) as the payment reference, and send confirmation to {opts.confirm_email}.</p>
            </div>
            <span className="cp-k">Already sent it? Let us know</span>
            <div className="cp-pay-report">
              <label>Date sent<input className="cp-input" type="date" value={sentOn} max={todayYmd()} onChange={(e) => setSentOn(e.target.value)} /></label>
              <label>Note (optional)<input className="cp-input" value={note} maxLength={1000} placeholder="e.g. sent from our Chase account" onChange={(e) => setNote(e.target.value)} /></label>
            </div>
            {err && <p className="cp-inv-msg">{err}</p>}
            <div className="cp-pay-foot">
              <button type="button" className="cp-pill" disabled={busy} onClick={() => { setErr(''); setStep('select'); }}>Back</button>
              <button type="button" className="cp-btn-sm cp-pay-go" disabled={busy || !canGo || opts.bank.demo} onClick={() => reportBank('bank')}>{busy ? 'Sending…' : 'I’ve sent this payment'}</button>
            </div>
          </div>
        )}

        {step === 'wise' && (
          <div className="cp-pay-body">
            <Summary withFee={false} />
            <div className="cp-pay-bank">
              <div className="cp-pay-bank-head"><span className="cp-k">Pay through Wise</span></div>
              <p className="cp-pay-hint" style={{ marginTop: 0 }}>Opens our Wise payment page in a new tab. Enter the amount above and include the invoice number{lines.length === 1 ? '' : 's'} ({lines.map((l) => l.number).join(', ')}) as the reference.</p>
              <a className="cp-btn-sm cp-pay-wise" href={opts.wise.url} target="_blank" rel="noopener noreferrer">Open Wise to pay {money(subtotal, ccy)}</a>
            </div>
            <span className="cp-k">Already paid with Wise? Let us know</span>
            <div className="cp-pay-report">
              <label>Date paid<input className="cp-input" type="date" value={sentOn} max={todayYmd()} onChange={(e) => setSentOn(e.target.value)} /></label>
              <label>Note (optional)<input className="cp-input" value={note} maxLength={1000} placeholder="e.g. Wise transfer #123456789" onChange={(e) => setNote(e.target.value)} /></label>
            </div>
            {err && <p className="cp-inv-msg">{err}</p>}
            <div className="cp-pay-foot">
              <button type="button" className="cp-pill" disabled={busy} onClick={() => { setErr(''); setStep('select'); }}>Back</button>
              <button type="button" className="cp-btn-sm cp-pay-go" disabled={busy || !canGo} onClick={() => reportBank('wise')}>{busy ? 'Sending…' : 'I’ve paid with Wise'}</button>
            </div>
          </div>
        )}

        {step === 'check' && (
          <div className="cp-pay-body">
            <Summary withFee={false} />
            <div className="cp-pay-bank">
              <div className="cp-pay-bank-head"><span className="cp-k">Make your check payable and mail to</span></div>
              <pre>{opts.check.details}</pre>
              <p className="cp-pay-hint">Please write the invoice number{lines.length === 1 ? '' : 's'} ({lines.map((l) => l.number).join(', ')}) on the memo line so we can apply it to the right invoice{lines.length === 1 ? '' : 's'}.</p>
            </div>
            <span className="cp-k">Already mailed it? Let us know</span>
            <div className="cp-pay-report">
              <label>Date mailed<input className="cp-input" type="date" value={sentOn} max={todayYmd()} onChange={(e) => setSentOn(e.target.value)} /></label>
              <label>Check # / note (optional)<input className="cp-input" value={note} maxLength={1000} placeholder="e.g. check #1042" onChange={(e) => setNote(e.target.value)} /></label>
            </div>
            {err && <p className="cp-inv-msg">{err}</p>}
            <div className="cp-pay-foot">
              <button type="button" className="cp-pill" disabled={busy} onClick={() => { setErr(''); setStep('select'); }}>Back</button>
              <button type="button" className="cp-btn-sm cp-pay-go" disabled={busy || !canGo || opts.check.demo} onClick={() => reportBank('check')}>{busy ? 'Sending…' : 'I’ve mailed a check'}</button>
            </div>
          </div>
        )}

        {step === 'done' && result && (
          <div className="cp-pay-body">
            {result.kind === 'card' ? (
              <>
                <p className="cp-pay-ok">✓ {result.processing ? 'Payment submitted' : 'Thank you — your payment went through.'}</p>
                {result.processing && <p className="cp-pay-hint">{result.msg}</p>}
                {result.p && (
                  <div className="cp-pay-sum">
                    {result.p.invoices.map((l) => <div key={l.number}><span>Invoice {l.number}</span><span>{money(l.amount, ccy)}</span></div>)}
                    {result.p.fee_amount > 0 && <div><span>Card fee ({result.p.fee_pct}%)</span><span>{money(result.p.fee_amount, ccy)}</span></div>}
                    {result.p.fee_refunded > 0 && <div><span>Fee refunded (debit card)</span><span>−{money(result.p.fee_refunded, ccy)}</span></div>}
                    <div className="cp-pay-total"><span>Charged{result.p.card_last4 ? ` to •••• ${result.p.card_last4}` : ''}</span><span>{money(result.p.charged, ccy)}</span></div>
                  </div>
                )}
                <p className="cp-pay-hint">Reference {result.p && result.p.ref}. A receipt is on its way to your email. Invoice balances update in a few minutes.</p>
              </>
            ) : (
              <>
                <p className="cp-pay-ok">✓ We’ve let the XYZ team know.</p>
                <p className="cp-pay-hint">We’ll apply your {{ check: 'check', wise: 'Wise payment' }[result.kind] || 'transfer'} of {money(result.p && result.p.subtotal, ccy)} to your invoices once it arrives (reference {result.p && result.p.ref}).</p>
              </>
            )}
            <div className="cp-pay-foot"><button type="button" className="cp-btn-sm" onClick={onDone}>Done</button></div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PayInvoices({ clientId, data, onPaid, autoOpen, onAutoOpened }) {
  const [opts, setOpts] = useState(null);
  const [hist, setHist] = useState([]);
  const [show, setShow] = useState(false);
  const load = useCallback(() => {
    if (!clientId) return;
    apiFetch(`/api/portal/pay/options?client_id=${encodeURIComponent(clientId)}`).then((r) => (r.ok ? r.json() : null)).then(setOpts).catch(() => setOpts(null));
    apiFetch(`/api/portal/pay/history?client_id=${encodeURIComponent(clientId)}`).then((r) => (r.ok ? r.json() : null)).then((j) => setHist((j && j.payments) || [])).catch(() => setHist([]));
  }, [clientId]);
  useEffect(() => { setOpts(null); setHist([]); load(); }, [load]);
  // @@PORTAL_PAY_v124_1@@ header "Make a Payment": a truthy autoOpen opens the modal once (if anything is open to pay),
  // then onAutoOpened() lets the page clear it — so a reload/remount after paying never re-opens it.
  const ready = !!(opts && opts.available && data && data.available);
  const hasOpen = !!(data && data.invoices && data.invoices.some((i) => OPENISH.has(i.status) && Number(i.balance) > 0));
  useEffect(() => {
    if (!autoOpen || !ready) return;
    if (hasOpen) setShow(true);
    if (onAutoOpened) onAutoOpened();
  }, [autoOpen, ready, hasOpen, onAutoOpened]);

  const ccy = (data && data.currency_code) || 'USD';
  const open = useMemo(() => ((data && data.invoices) || [])
    .filter((i) => OPENISH.has(i.status) && Number(i.balance) > 0)
    .sort((a, b) => String(a.due_date || '').localeCompare(String(b.due_date || ''))), [data]);
  if (!opts || !opts.available || !data || !data.available) return null;
  const due = round2(open.reduce((a, i) => a + Number(i.balance), 0));
  const recent = hist.slice(0, 3);

  return (
    <div className="cp-pay-bar">
      <div className="cp-pay-bar-main">
        <div>
          <div className="cp-pay-bar-title">{open.length ? `${money(due, ccy)} open on ${open.length} invoice${open.length === 1 ? '' : 's'}` : 'Nothing to pay right now'}</div>
          <div className="cp-pay-hint" style={{ margin: 0 }}>
            {(() => { const m = [opts.card.enabled && 'card', opts.bank.enabled && 'bank transfer', opts.wise && opts.wise.enabled && 'Wise', opts.check && opts.check.enabled && 'check'].filter(Boolean);
              return 'Pay by ' + (m.length > 1 ? m.slice(0, -1).join(', ') + ' or ' + m[m.length - 1] : m[0] || '…'); })()}
            {opts.card.enabled && Number(opts.card.fee_pct) > 0 ? ` · ${opts.card.fee_pct}% surcharge on credit cards only` : ''}
          </div>
        </div>
        <button type="button" className="cp-btn-sm cp-pay-go" disabled={!open.length} onClick={() => setShow(true)}>Pay invoices</button>
      </div>
      {recent.length > 0 && (
        <div className="cp-pay-recent">
          {recent.map((p) => (
            <span key={p.id}>
              {p.method === 'card' ? `Paid ${money(p.charged, p.currency_code)} by card` : p.method === 'check' ? `Check of ${money(p.subtotal, p.currency_code)} mailed` : p.method === 'wise' ? `Wise payment of ${money(p.subtotal, p.currency_code)} reported` : `Bank transfer of ${money(p.subtotal, p.currency_code)} reported`}
              {' · '}{new Date(p.created_at).toLocaleDateString()} · {p.invoices.map((l) => l.number).join(', ')}
            </span>
          ))}
        </div>
      )}
      {show && (
        <PayModal clientId={clientId} ccy={ccy} open={open} opts={opts}
          onClose={() => setShow(false)}
          onDone={() => { setShow(false); load(); if (onPaid) onPaid(); }} />
      )}
    </div>
  );
}
